import React, { useEffect, useMemo, useState } from 'react';
import { filter, find, includes, isEmpty, isUndefined, map, reduce, trim, union } from 'lodash';

import { memberIsPending } from '@affiliates/components';
import {
    IAffiliateLinkMember,
    IPromoCodeMember,
    MemberStatus,
    OfferMemberStatus,
    TMemberTableCSVRow,
    TOfferMember,
    TSelectedAffliate,
} from '@affiliates/components/MemberTable/types';
import { TOfferStats } from '@affiliates/components/OfferDetails/types';
import { useSTAPaymentAppContext } from '@affiliates/contexts';
import { IHeader, useAdvertiserShopifyScopeCheck, useExportToCsv } from '@affiliates/hooks';
import {
    GetOfferById,
    GetOfferById_offer_links_affiliateStats,
} from '@affiliates/queries/types/GetOfferById';
import { TMember, TMode } from '@affiliates/components/MembersWizard/types';
import { roundToNearestCent } from '@affiliates/utils';
import { EventName, logger } from '@common';
import { IDateRangeSettings } from '@frontend/app/components';
import { useEventContext } from '@frontend/app/context';
import { useMapDateRangeToVariables } from '@frontend/app/hooks';
import {
    GetMultipleShopifyStats,
    GetMultipleShopifyStats_storeStats,
} from '@affiliates/queries/types/GetMultipleShopifyStats';
import { useApolloClient, useQuery } from '@apollo/client';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { message } from 'antd';
import { OFFER_PROMO_CODE_STATUS, OFFER_SOURCE, AFFILIATE_STATUS, OFFER_TYPE } from '../types/globalTypes';
import { AFFILIATE_OFFER_LINK_STATUS } from '../constants/offers';
import {
    GET_MULTIPLE_SHOPIFY_STATS_QUERY,
    GET_OFFER_AFFILIATES_STATS_MULTIPLE_SHOPIFY,
    PAUSE_BULK_AFFILIATE_OFFERS_LINK_ASYNC,
    GET_OFFER_BY_ID_WITH_STATS_QUERY,
    GET_OFFER_AFFILIATES_QUERY,
    GET_ALL_BOOST_DATA_QUERY,
} from '../queries';
import {
    GetOfferAffliatesStatsMultipleShopify,
    GetOfferAffliatesStatsMultipleShopify_affiliatesStats,
} from '../queries/types/GetOfferAffliatesStatsMultipleShopify';
import { useDeleteAffiliateFromAnOffer } from './useDeleteAffiliateFromAnOffer';
import { GetOfferAffiliates, GetOfferAffiliates_affiliates_promoAffiliates } from '../queries/types/GetOfferAffiliates';
import { GetAllBoostData, GetAllBoostData_boostDataForAllAffiliatesInOffer } from '../queries/types/GetAllBoostData';

export enum OfferDetailsQueriesStatus {
    Failed,
    Loading,
    Ready,
}

interface IOfferDetailsQueriesLoading {
    status: OfferDetailsQueriesStatus.Loading;
}

interface IOfferDetailsQueriesFailed {
    error: Error;
    status: OfferDetailsQueriesStatus.Failed;
}

interface IOfferDetailsQueriesReady {
    failedPromoCodeErrors: OFFER_PROMO_CODE_STATUS[];
    failedMembers: {
        members: readonly TMember[];
        dismissFailedMembers: () => void;
        showFailedMembers: () => void;
        visible: boolean;
    };
    missingShopifyCredentials: boolean | null;
    offerDetails: GetOfferById['offer'];
    offerMembers: readonly TOfferMember[];
    deletedMembers: readonly TOfferMember[];
    offerStats: TOfferStats;
    multipleShopifyOfferStats: GetMultipleShopifyStats_storeStats[];
    memberTable: {
        buttonActions: {
            addMembers: () => void;
            export: (columns: IHeader[], data: readonly TMemberTableCSVRow[]) => void;
            pause: () => void;
            resume: (refreshDates: boolean) => void;
        };
        disableCompose: boolean;
        onSelectMembers: (memberIds: number[]) => void;
        selectedAffiliateIds: number[];
    };
    refresh: () => void;
    status: OfferDetailsQueriesStatus.Ready;
    updateMembers: {
        mode: TMode;
        onClose: () => void;
        refreshDatesOnly: boolean;
        selectedMembers: readonly TMember[];
        visible: boolean;
    };
    memberConversion: {
        visible: boolean;
        selectedMemberConversion: TSelectedAffliate;
        onClose: () => void;
    };
    updateManageOffer?: {
        mode: TMode;
        onClose: () => void;
        visible: boolean;
        selectedMembers: readonly TMember[];
        setIsVisibleSelectOffer: (isVisibleManageOffer: boolean) => void;
    };
    multipleShopifyAffiliateStats: {
        visible: boolean;
        affiliatesStats: GetOfferAffliatesStatsMultipleShopify_affiliatesStats[];
        toggle: () => void;
    };
    shopifyStoreName: string;
    affiliatesData?: GetOfferAffiliates['affiliates']; // Add affiliatesData to the return type
    boostData?: GetAllBoostData_boostDataForAllAffiliatesInOffer[]; // Add boostData to the return type
    isLoadingMembers: boolean;
}

type TOfferDetailsQueries = Readonly<
    IOfferDetailsQueriesFailed | IOfferDetailsQueriesLoading | IOfferDetailsQueriesReady
>;
const NULL_DATA = {
    offerStats: {
        avgSale: 0,
        clicks: 0,
        conversionRatio: 0,
        conversions: 0,
        payout: 0,
        payoutEarned: 0,
        payoutMade: 0,
        sales: 0,
        memberConversionCount: 0,
        membersWithPayoutDue: 0,
    },
    offerMembers: [],
    deletedMembers: [],
};

export const getStatusForAffiliateLinkMember = (member: GetOfferById_offer_links_affiliateStats): OfferMemberStatus => {
    const status = trim(member.linkStatus).toUpperCase();
    switch (status) {
        case AFFILIATE_OFFER_LINK_STATUS.ACTIVE:
            return OfferMemberStatus.ACTIVE;
        case AFFILIATE_OFFER_LINK_STATUS.DISABLED:
            return OfferMemberStatus.PAUSED;
        case AFFILIATE_OFFER_LINK_STATUS.PENDING:
            return OfferMemberStatus.PENDING;
        case AFFILIATE_OFFER_LINK_STATUS.PENDING_UNPAUSE:
            return OfferMemberStatus.PENDING_UPDATE;
        case AFFILIATE_OFFER_LINK_STATUS.PENDING_PAUSE:
            return OfferMemberStatus.PENDING_DELETION;
        case AFFILIATE_OFFER_LINK_STATUS.FAILED_UPDATE:
            return OfferMemberStatus.FAILED_UPDATE;
        case AFFILIATE_OFFER_LINK_STATUS.DELETING:
            return OfferMemberStatus.DELETING;
    }
    return OfferMemberStatus.UNKNOWN;
};

export const getStatusForPromoCodeMember = (
    member: GetOfferAffiliates_affiliates_promoAffiliates,
): OfferMemberStatus => {
    switch (member.affiliateOfferPromoStatus) {
        case OFFER_PROMO_CODE_STATUS.PENDING:
            return OfferMemberStatus.PENDING;
        case OFFER_PROMO_CODE_STATUS.PENDING_DELETION:
            return OfferMemberStatus.PENDING_DELETION;
        case OFFER_PROMO_CODE_STATUS.PENDING_UPDATE:
            return OfferMemberStatus.PENDING_UPDATE;
        case OFFER_PROMO_CODE_STATUS.DELETED:
            return OfferMemberStatus.DEACTIVATED;
        case OFFER_PROMO_CODE_STATUS.FAILED_DUPLICATE:
        case OFFER_PROMO_CODE_STATUS.FAILED_OTHER:
        case OFFER_PROMO_CODE_STATUS.FAILED_TOO_LONG:
        case OFFER_PROMO_CODE_STATUS.FAILED_DELETION:
            return OfferMemberStatus.FAILED;
        case OFFER_PROMO_CODE_STATUS.EXPIRED:
            return OfferMemberStatus.EXPIRED;
        case OFFER_PROMO_CODE_STATUS.SUCCESS:
            return OfferMemberStatus.ACTIVE;
    }
    // should not reach here but just in case we receive something else from the
    // server, we should return *something*
    return OfferMemberStatus.UNKNOWN;
};

export const getMemberStatus = (memberStatus: AFFILIATE_STATUS): MemberStatus => {
    switch (memberStatus) {
        case AFFILIATE_STATUS.ACTIVE:
            return MemberStatus.ACTIVE;
        case AFFILIATE_STATUS.DELETED:
            return MemberStatus.DELETED;
    }
    // should not reach here but just in case we receive something else from the
    // server, we should return *something*
    return MemberStatus.ACTIVE;
};

export const useRefreshOfferDetailsQueries = (
    offerId: number,
    dateRangeSettings: IDateRangeSettings,
    isEnabledMultipleShopify: boolean,
    onDeletedMembersChange?: (members: readonly TOfferMember[]) => void,
): TOfferDetailsQueries => {
    const [multipleOfferStats, setMultipleOfferStats] = useState<GetMultipleShopifyStats_storeStats[]>(null);
    const [affiliatesStats, setAffiliatesStats] = useState<GetOfferAffliatesStatsMultipleShopify_affiliatesStats[]>(null);
    const [basicData, setBasicData] = useState<any>(null);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [affiliatesData, setAffiliatesData] = useState<any>(null);
    const [localBoostData, setLocalBoostData] = useState<GetAllBoostData>(null);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { dateFilter: paymentDateFilter, setOffers, setPaymentsDue, setRefreshCurrentDashboard, setRefreshCurrentTable } =
        useSTAPaymentAppContext();

    const dateRangeVariables = useMapDateRangeToVariables(dateRangeSettings.dateRange);
    const paymentDateRangeVariables = useMapDateRangeToVariables(paymentDateFilter.dateRange);

    const client = useApolloClient();
    const { setDisableOfferEdit, setOfferMembers, setBoostData } = useOfferDetailsContext();

    // Use useQuery for basic data (includes offer stats)
    const { data: basicDataFromQuery, refetch: refetchBasicData } = useQuery(GET_OFFER_BY_ID_WITH_STATS_QUERY, {
        variables: {
            id: offerId,
            startDate: dateRangeVariables.startDate,
            endDate: dateRangeVariables.endDate,
        },
        fetchPolicy: 'network-only',
        skip: !offerId,
        onError: (err) => {
            setError(err);
            logger.error(err);
        },
    });
    // Use useQuery for payment basic data (includes offer stats)
    const { data: paymentDataFromQuery } = useQuery(GET_OFFER_BY_ID_WITH_STATS_QUERY, {
        variables: {
            id: offerId,
            startDate: paymentDateRangeVariables.startDate,
            endDate: paymentDateRangeVariables.endDate,
        },
        fetchPolicy: 'network-only',
        skip: !offerId,
        onError: (err) => {
            setError(err);
            logger.error(err);
        },
    });

    // Keep the existing state for backward compatibility
    useEffect(() => {
        if (basicDataFromQuery) {
            setBasicData(basicDataFromQuery);
            setError(null);
        }
    }, [basicDataFromQuery]);

    useEffect(() => {
        if (paymentDataFromQuery) {
            setPaymentData(paymentDataFromQuery);
            setError(null);
        }
    }, [paymentDataFromQuery]);

    // Use useQuery for affiliates data
    const offer = basicData?.offer?.offer;
    const isLinkOffer = offer?.type === OFFER_TYPE.LINK;
    const isPromoLinkOffer = offer?.isPromoLink;

    const { data: affiliatesDataFromQuery, refetch: refetchAffiliatesData } = useQuery(GET_OFFER_AFFILIATES_QUERY, {
        variables: {
            offerId,
            startDate: dateRangeVariables.startDate,
            endDate: dateRangeVariables.endDate,
            isLinkOffer,
            isPromoLinkOffer,
        },
        fetchPolicy: 'network-only',
        skip: !basicData?.offer?.offer || !offerId,
        onError: (err) => {
            logger.error(err);
        },
    });

    const { data: boostDataFromQuery, refetch: refetchBoostData } = useQuery(GET_ALL_BOOST_DATA_QUERY, {
        variables: {
            offerId,
        },
        fetchPolicy: 'network-only',
        skip: !basicData?.offer?.offer?.boosts?.length || !offerId,
        onError: (err) => {
            logger.error(err);
        },
    });

    // Keep the existing state for backward compatibility
    useEffect(() => {
        if (affiliatesDataFromQuery) {
            setAffiliatesData(affiliatesDataFromQuery);
        }
    }, [affiliatesDataFromQuery]);

    // Keep boost data state for backward compatibility
    useEffect(() => {
        if (boostDataFromQuery) {
            setLocalBoostData(boostDataFromQuery);
            setBoostData(boostDataFromQuery.boostDataForAllAffiliatesInOffer || []);
        }
    }, [boostDataFromQuery, setBoostData]);

    // Handle loading state for affiliates data
    useEffect(() => {
        if (basicData?.offer?.offer && !affiliatesDataFromQuery) {
            setIsLoadingMembers(true);
        } else {
            setIsLoadingMembers(false);
        }
    }, [basicData, affiliatesDataFromQuery]);

    // Fetch boost data if offer has boosts
    useEffect(() => {
        if (!basicData?.offer?.offer?.boosts?.length) {
            return;
        }

        const fetchBoostData = async () => {
            try {
                const result = await client.query({
                    query: GET_ALL_BOOST_DATA_QUERY,
                    variables: {
                        offerId,
                    },
                    fetchPolicy: 'network-only',
                });
                setLocalBoostData(result.data);
                setBoostData(result.data.boostDataForAllAffiliatesInOffer || []);
            } catch (err) {
                logger.error(err);
            }
        };

        fetchBoostData();
    }, [basicData, client, offerId, setBoostData]);

    // Fetch multiple Shopify stats if enabled
    useEffect(() => {
        if (isEnabledMultipleShopify && basicData?.offer?.offer?.promos?.[0]?.connectedClientMetadata?.length) {
            client
                .query<GetMultipleShopifyStats>({
                    query: GET_MULTIPLE_SHOPIFY_STATS_QUERY,
                    variables: {
                        offerId,
                        ...dateRangeVariables,
                    },
                    fetchPolicy: 'network-only',
                })
                .then((multipleShopifyStatsData) => {
                    setMultipleOfferStats(multipleShopifyStatsData?.data?.storeStats);
                })
                .catch((e) => {
                    logger.error(e);
                });

            client
                .query<GetOfferAffliatesStatsMultipleShopify>({
                    query: GET_OFFER_AFFILIATES_STATS_MULTIPLE_SHOPIFY,
                    variables: {
                        offerId,
                        ...dateRangeVariables,
                    },
                    fetchPolicy: 'network-only',
                })
                .then((multipleShopifyStatsData) => {
                    setAffiliatesStats(multipleShopifyStatsData?.data?.affiliatesStats);
                })
                .catch((e) => {
                    logger.error(e);
                });
        }
    }, [client, dateRangeVariables, offerId, isEnabledMultipleShopify, basicData]);
    const advertiserShopifyScopeCheck = useAdvertiserShopifyScopeCheck({
        skip: !basicData?.offer?.offer || isEmpty(basicData?.offer?.offer?.promos),
    });
    const missingShopifyCredentials = useMemo(() => {
        if (!advertiserShopifyScopeCheck.loading && !advertiserShopifyScopeCheck.error) {
            return !advertiserShopifyScopeCheck.data?.advertiser?.hasShopifyScopes;
        }
        return null;
    }, [advertiserShopifyScopeCheck]);

    useEffect(() => {
        const newRefreshCurrentDashboard = () => {
            // Refetch basic data using the refetch function
            refetchBasicData();
        };
        setRefreshCurrentDashboard(() => newRefreshCurrentDashboard);
        setRefreshCurrentTable(() => newRefreshCurrentDashboard);
    }, [dateRangeVariables, offerId, client, setRefreshCurrentDashboard, setRefreshCurrentTable, refetchBasicData, refetchAffiliatesData, refetchBoostData]);
    useEffect(() => {
        if (!basicData?.offer) {
            setPaymentsDue({ state: 'loading' });
            return;
        }

        const { promoStats, linkStats } = paymentData?.offer || basicData.offer;
        const offer = paymentData?.offer.offer || basicData.offer.offer;

        // check for offer.isPromoLink
        if (offer.isPromoLink && promoStats && linkStats) {
            setPaymentsDue({
                paymentsDue: Math.max(
                    0,
                    roundToNearestCent(
                        promoStats.payoutEarned - promoStats.payoutMade + (linkStats.payoutEarned - linkStats.payoutMade),
                    ),
                ),
                state: 'ready',
            });
        } else if (promoStats) {
            setPaymentsDue({
                paymentsDue: Math.max(0, roundToNearestCent(promoStats.payoutEarned - promoStats.payoutMade)),
                state: 'ready',
            });
        } else if (linkStats) {
            setPaymentsDue({
                paymentsDue: Math.max(0, roundToNearestCent(linkStats.payoutEarned - linkStats.payoutMade)),
                state: 'ready',
            });
        }
    }, [paymentData, offerId, setPaymentsDue, basicData]);
    useEffect(() => {
        setDisableOfferEdit(true);
        const offer = basicData?.offer?.offer;
        if (!isEmpty(offer)) {
            const contextOffer = {
                id: offer.id,
                name: offer.name,
                source: isEmpty(offer.links) ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE,
            };
            setOffers([contextOffer]);
        }
    }, [basicData, setOffers, setDisableOfferEdit]);

    let source = OFFER_SOURCE.SHOPIFY;
    if (basicData?.offer?.offer) {
        if (isEmpty(basicData.offer.offer.promos) && !isEmpty(basicData.offer.offer.links)) {
            source = OFFER_SOURCE.TUNE;
        }
    }

    const [selectedMemberIds, updatedSelectedMemberIds] = useState<number[]>([]);
    const exportToCsv = useExportToCsv();
    // update members wizard settings
    const [updateWizardMode, setUpdateWizardMode] = useState<TMode>('add');
    const [updateWizardVisible, setUpdateWizardVisible] = useState(false);
    const [isVisibleManageOffer, setIsVisibleSelectOffer] = useState(false);
    const [isVisibleMemberConversion, setIsVisibleMemberConversion] = useState(false);
    const [selectedMemberConversion, setSelectedMemberConversion] = useState<TSelectedAffliate | null>(null);
    const [refreshDatesOnly, setRefreshDatesOnly] = useState(false);
    const [showStoreLevelAffiliateStats, setShowStoreLevelAffiliateStats] = useState(false);
    const [deleteMemberFromAnOffer] = useDeleteAffiliateFromAnOffer();

    const addEvent = useEventContext();
    const buttonActions = useMemo(
        () => ({
            addMembers: () => {
                switch (source) {
                    case OFFER_SOURCE.TUNE:
                        setUpdateWizardMode('add');
                        setUpdateWizardVisible(true);
                        break;
                    case OFFER_SOURCE.SHOPIFY:
                        setRefreshDatesOnly(false);
                        setUpdateWizardMode('add');
                        setUpdateWizardVisible(true);
                        break;
                }
            },
            export: (columns, data) => {
                addEvent(EventName.SalesTrackingTableExport, {
                    exportDate: new Date().toISOString(),
                    exportedTable: 'members',
                });
                exportToCsv(`affiliates-${Date.now()}`, columns, data);
            },
            pause: () => {
                switch (source) {
                    case OFFER_SOURCE.TUNE:
                        setUpdateWizardMode('deactivate');
                        setUpdateWizardVisible(true);
                        break;
                    case OFFER_SOURCE.SHOPIFY:
                        setRefreshDatesOnly(false);
                        setUpdateWizardMode('deactivate');
                        setUpdateWizardVisible(true);
                        break;
                }
            },
            resume: (refreshDates: boolean) => {
                switch (source) {
                    case OFFER_SOURCE.TUNE:
                        setUpdateWizardMode('refresh');
                        setUpdateWizardVisible(true);
                        break;
                    case OFFER_SOURCE.SHOPIFY:
                        setRefreshDatesOnly(refreshDates);
                        setUpdateWizardMode('refresh');
                        setUpdateWizardVisible(true);
                        break;
                }
            },
            refresh: () => {
                setIsVisibleSelectOffer(true);
            },
            openMemberConversionDrawer: (selectedAffliate: TSelectedAffliate) => {
                setIsVisibleMemberConversion(true);
                setSelectedMemberConversion(selectedAffliate);
            },
            storeLevelAffiliateStats: () => {
                setShowStoreLevelAffiliateStats(!showStoreLevelAffiliateStats);
            },
            deleteAffiliatesFromAnOffer: async (
                selectedMemberIds: number[],
                offerId: number,
                offerSource: string,
                setIsDeletedAction: (isDeletedAction: boolean) => void,
            ) => {
                try {
                    if (offerSource === 'TUNE') {
                        await client
                            .mutate({
                                mutation: PAUSE_BULK_AFFILIATE_OFFERS_LINK_ASYNC,
                                variables: {
                                    ids: selectedMemberIds,
                                    removeAffiliates: true,
                                },
                            })
                            .catch((e) => {
                                setIsDeletedAction(false);
                                if (e.graphQLErrors && e.graphQLErrors[0]) {
                                    message.error(e.graphQLErrors[0].message);
                                } else if (e.networkError) {
                                    message.error(`Network error: ${e.networkError.message}`);
                                } else {
                                    message.error('An unexpected error occurred');
                                }
                            });
                    } else if (offerSource === 'SHOPIFY') {
                        await deleteMemberFromAnOffer({
                            variables: {
                                id: offerId,
                                affiliates: selectedMemberIds.map((e) => ({ id: e })),
                                removeAffiliates: true,
                            },
                            onError: (e) => {
                                setIsDeletedAction(false);
                                if (e.graphQLErrors && e.graphQLErrors[0]) {
                                    message.error(e.graphQLErrors[0].message);
                                } else if (e.networkError) {
                                    message.error(`Network error: ${e.networkError.message}`);
                                } else {
                                    message.error('An unexpected error occurred');
                                }
                            },
                        });
                    }
                } catch (e) {
                    setIsDeletedAction(false);
                }
            },
        }),
        [showStoreLevelAffiliateStats, addEvent, exportToCsv, source, deleteMemberFromAnOffer, client],
    );

    const { offerMembers, offerStats, deletedMembers } = useMemo((): {
        offerMembers: IOfferDetailsQueriesReady['offerMembers'];
        offerStats: IOfferDetailsQueriesReady['offerStats'];
        deletedMembers: IOfferDetailsQueriesReady['deletedMembers'];
    } => {
        if (!basicData?.offer) {
            return NULL_DATA;
        }

        const { promoStats, linkStats } = basicData.offer;
        const offer = basicData.offer.offer;

        // Calculate offer stats from basic data
        const calculatedOfferStats = (() => {
            if (offer.isPromoLink && promoStats && linkStats) {
                return {
                    clicks: linkStats.clicks || 0,
                    conversions: promoStats.conversions + (linkStats.nonDuplicateConversions || 0),
                    payoutEarned: promoStats.payoutEarned + (linkStats.payoutEarned || 0),
                    payoutMade: promoStats.payoutMade + (linkStats.payoutMade || 0),
                    sales: promoStats.sales + (linkStats.sales || 0),
                    avgSale:
                        promoStats.conversions + (linkStats.nonDuplicateConversions || 0)
                            ? (promoStats.sales + (linkStats.sales || 0)) /
                            (promoStats.conversions + (linkStats.nonDuplicateConversions || 0))
                            : 0,
                    salesBase: promoStats.salesBase + (linkStats.salesBase || 0),
                    avgSaleBase:
                        promoStats.conversions + (linkStats.nonDuplicateConversions || 0)
                            ? (promoStats.salesBase + (linkStats.salesBase || 0)) /
                            (promoStats.conversions + (linkStats.nonDuplicateConversions || 0))
                            : 0,
                    payoutEarnedBase: promoStats.payoutEarnedBase + (linkStats.payoutEarnedBase || 0),
                    currencies: union([...promoStats.baseCurrencies, ...(linkStats.baseCurrencies || [])]),
                    memberConversionCount: null, // Will be calculated when affiliates data is loaded
                    membersWithPayoutDue: null, // Will be calculated when affiliates data is loaded
                };
            } else if (promoStats) {
                return {
                    ...promoStats,
                    currencies: promoStats.baseCurrencies,
                    memberConversionCount: null,
                    membersWithPayoutDue: null,
                };
            } else if (linkStats) {
                return {
                    ...linkStats,
                    currencies: linkStats.baseCurrencies,
                    memberConversionCount: null,
                    membersWithPayoutDue: null,
                };
            }
            return NULL_DATA.offerStats;
        })();

        // Process affiliates data if available
        if (affiliatesData?.affiliates) {
            const { promoAffiliates, linkAffiliatesStats } = affiliatesData.affiliates;
            const allMembers = [...(promoAffiliates || []), ...(linkAffiliatesStats || [])];

            calculatedOfferStats.memberConversionCount = allMembers.filter((member) => member.conversions > 0).length;
            calculatedOfferStats.membersWithPayoutDue = allMembers.filter(
                (member: IAffiliateLinkMember | IPromoCodeMember) => member.payoutEarned - member.payoutMade > 0,
            ).length;
        }
        let processedOfferMembers = [];
        let processedDeletedMembers = [];

        if (affiliatesData?.affiliates) {
            const { promoAffiliates, linkAffiliatesStats, linkAffiliates } = affiliatesData.affiliates;

            switch (source) {
                case OFFER_SOURCE.SHOPIFY:
                    if (promoAffiliates) {
                        // Process promo affiliates (Shopify) - similar to previous implementation
                        const processedPromoMembers = map(promoAffiliates, (member) => {
                            const promoMember = find(
                                promoAffiliates,
                                (affiliate) => affiliate.affiliateId === member.affiliateId,
                            );
                            const linkMemberStat = offer.isPromoLink
                                ? find(
                                    linkAffiliatesStats || [],
                                    (linkAffiliate) => linkAffiliate.affiliateId === member.affiliateId,
                                )
                                : null;

                            if (!isUndefined(promoMember) && promoMember.deletedDate === null) {
                                return {
                                    affiliateId: member.affiliateId,
                                    affiliateOfferId: promoMember.id,
                                    avgSale: member.avgSale,
                                    code: member.affiliateCode,
                                    payoutId: promoMember.offerPayoutId,
                                    codeEndDate: promoMember.endDate,
                                    codeStartDate: promoMember.startDate,
                                    conversions: member.conversions,
                                    email: member.affiliateEmail,
                                    imageUrl: member.affiliateImageUrl,
                                    memberId: member.aspirexMemberId,
                                    name: member.affiliateName,
                                    payoutEarned: member.payoutEarned,
                                    payoutMade: member.payoutMade,
                                    sales: member.sales,
                                    salesBase: member.salesBase,
                                    avgSaleBase: member.avgSaleBase,
                                    payoutEarnedBase: member.payoutEarnedBase,
                                    currencies: promoStats?.baseCurrencies,
                                    source: OFFER_SOURCE.SHOPIFY,
                                    status: promoMember ? getStatusForPromoCodeMember(promoMember) : OfferMemberStatus.UNKNOWN,
                                    memberStatus: promoMember ? getMemberStatus(promoMember?.memberStatus) : MemberStatus.ACTIVE,
                                    promoCodeStatus: promoMember.status,
                                    externalDiscountCodeGid: promoMember.externalDiscountCodeGId,
                                    providerMetadata: promoMember.providerMetadata,
                                    createdDate: promoMember.codeCreatedDate,
                                    deletedDate: promoMember.deletedDate,
                                    offerAutomationConfigId: promoMember?.offerAutomationConfigId,
                                    boostDataForAffiliateOffer: promoMember?.boostDataForAffiliateOffer,
                                    affiliateOfferPromoId: promoMember?.affiliateOfferPromoId,
                                    baseCurrencies: promoMember?.baseCurrencies,
                                    ...(offer.isPromoLink
                                        ? {
                                            affiliateShortLink: find(
                                                linkAffiliatesStats || [],
                                                (affiliate) => affiliate.affiliateId === member.affiliateId,
                                            )?.affiliateShortLink,
                                            affiliateLink: find(
                                                linkAffiliatesStats || [],
                                                (affiliate) => affiliate.affiliateId === member.affiliateId,
                                            )?.affiliateLink,
                                            clicks: find(
                                                linkAffiliatesStats || [],
                                                (affiliate) => affiliate.affiliateId === member.affiliateId,
                                            )?.clicks,
                                            conversions: member.conversions + (linkMemberStat?.nonDuplicateConversions || 0),
                                            payoutEarned: member.payoutEarned + (linkMemberStat?.payoutEarned || 0),
                                            payoutMade: member.payoutMade + (linkMemberStat?.payoutMade || 0),
                                            sales: member.sales + (linkMemberStat?.sales || 0),
                                            avgSale:
                                                (member.sales + (linkMemberStat?.sales || 0)) /
                                                (member.conversions + (linkMemberStat?.nonDuplicateConversions || 0)),
                                            avgSaleBase:
                                                member.conversions + (linkMemberStat?.nonDuplicateConversions || 0)
                                                    ? (member.salesBase + (linkMemberStat?.salesBase || 0)) /
                                                    (member.conversions + (linkMemberStat?.nonDuplicateConversions || 0))
                                                    : 0,
                                            salesBase: member.salesBase + (linkMemberStat?.salesBase || 0),
                                            payoutEarnedBase: member.payoutEarnedBase + (linkMemberStat?.payoutEarnedBase || 0),
                                        }
                                        : {}),
                                };
                            }
                        }).filter(Boolean);

                        const deletedPromoMembers = map(promoAffiliates, (member) => {
                            const promoMember = find(
                                promoAffiliates,
                                (affiliate) => affiliate.affiliateId === member.affiliateId,
                            );
                            if (!isUndefined(promoMember) && promoMember.deletedDate !== null) {
                                return {
                                    affiliateId: member.affiliateId,
                                    affiliateOfferId: promoMember.id,
                                    avgSale: member.avgSale,
                                    code: member.affiliateCode,
                                    payoutId: promoMember.offerPayoutId,
                                    codeEndDate: promoMember.endDate,
                                    codeStartDate: promoMember.startDate,
                                    conversions: member.conversions,
                                    email: member.affiliateEmail,
                                    imageUrl: member.affiliateImageUrl,
                                    memberId: member.aspirexMemberId,
                                    name: member.affiliateName,
                                    payoutEarned: member.payoutEarned,
                                    payoutMade: member.payoutMade,
                                    sales: member.sales,
                                    salesBase: member.salesBase,
                                    avgSaleBase: member.avgSaleBase,
                                    payoutEarnedBase: member.payoutEarnedBase,
                                    currencies: promoStats?.baseCurrencies,
                                    source: OFFER_SOURCE.SHOPIFY,
                                    status: promoMember ? getStatusForPromoCodeMember(promoMember) : OfferMemberStatus.UNKNOWN,
                                    memberStatus: promoMember ? getMemberStatus(promoMember?.memberStatus) : MemberStatus.ACTIVE,
                                    promoCodeStatus: promoMember.status,
                                    externalDiscountCodeGid: promoMember.externalDiscountCodeGId,
                                    providerMetadata: promoMember.providerMetadata,
                                    createdDate: promoMember.codeCreatedDate,
                                    deletedDate: promoMember.deletedDate,
                                    offerAutomationConfigId: promoMember?.offerAutomationConfigId,
                                    boostDataForAffiliateOffer: promoMember?.boostDataForAffiliateOffer,
                                    affiliateOfferPromoId: promoMember?.affiliateOfferPromoId,
                                    baseCurrencies: promoMember?.baseCurrencies,
                                };
                            }
                        }).filter(Boolean);

                        processedOfferMembers = [...processedOfferMembers, ...processedPromoMembers];
                        processedDeletedMembers = [...processedDeletedMembers, ...deletedPromoMembers];
                    }
                    break;

                case OFFER_SOURCE.TUNE:
                    if (linkAffiliates) {
                        // Process link affiliates (TUNE) - use linkAffiliates for the main data
                        const processedLinkMembers = map(linkAffiliates, (member): IAffiliateLinkMember => {
                            // Find corresponding stats data
                            const statsData = find(linkAffiliatesStats || [], (stat) => stat.affiliateId === member.affiliateId);

                            if (member && member.affiliateId && member.deletedDate === null) {
                                return {
                                    affiliateId: member.affiliateId,
                                    affiliateOfferId: member.id,
                                    memberAffiliateOfferId: member.id,
                                    affiliateLink: statsData?.affiliateLink || member.affiliateShortLink,
                                    affiliateShortLink: statsData?.affiliateShortLink || member.affiliateShortLink,
                                    avgSale: statsData?.avgSale || 0,
                                    payoutId: member.offerPayoutId,
                                    clicks: statsData?.clicks || 0,
                                    conversions: statsData?.conversions || 0,
                                    email: member.affiliate?.email || statsData?.affiliateEmail,
                                    imageUrl: member.affiliate?.profilePicture || statsData?.affiliateImageUrl,
                                    linkCreationDate: statsData?.linkCreationDate || member.createdDate,
                                    memberId: member.affiliate?.memberId || statsData?.aspirexMemberId,
                                    name: member.affiliate?.name || statsData?.affiliateName,
                                    payoutEarned: statsData?.payoutEarned || 0,
                                    payoutMade: statsData?.payoutMade || 0,
                                    sales: statsData?.sales || 0,
                                    source: OFFER_SOURCE.TUNE,
                                    status: getStatusForAffiliateLinkMember(statsData || member),
                                    memberStatus: member.affiliate ? getMemberStatus(member.affiliate.status) : MemberStatus.ACTIVE,
                                    linkStatusError: member.updateLog,
                                    additionalDeepLinks: member.affiliateDeeplinks || [],
                                    defaultDeeplink: member.deeplinkUrl,
                                    deletedDate: member.deletedDate,
                                    currencies: linkStats?.baseCurrencies,
                                    avgSaleBase: statsData?.avgSaleBase || 0,
                                    salesBase: statsData?.salesBase || 0,
                                    payoutEarnedBase: statsData?.payoutEarnedBase || 0,
                                    offerAutomationConfigId: member.offerAutomationConfigId,
                                    boostDataForAffiliateOffer: member.boostDataForAffiliateOffer || [],
                                    createdDate: member.createdDate,
                                };
                            }
                        }).filter(Boolean);

                        const deletedLinkMembers = map(linkAffiliates, (member): IAffiliateLinkMember => {
                            // Find corresponding stats data
                            const statsData = find(linkAffiliatesStats || [], (stat) => stat.affiliateId === member.affiliateId);

                            if (member && member.affiliateId && member.deletedDate !== null) {
                                return {
                                    affiliateId: member.affiliateId,
                                    affiliateOfferId: member.id,
                                    memberAffiliateOfferId: member.id,
                                    affiliateLink: statsData?.affiliateLink || member.affiliateShortLink,
                                    affiliateShortLink: statsData?.affiliateShortLink || member.affiliateShortLink,
                                    avgSale: statsData?.avgSale || 0,
                                    payoutId: member.offerPayoutId,
                                    clicks: statsData?.clicks || 0,
                                    conversions: statsData?.conversions || 0,
                                    email: member.affiliate?.email || statsData?.affiliateEmail,
                                    imageUrl: member.affiliate?.profilePicture || statsData?.affiliateImageUrl,
                                    linkCreationDate: statsData?.linkCreationDate || member.createdDate,
                                    memberId: member.affiliate?.memberId || statsData?.aspirexMemberId,
                                    name: member.affiliate?.name || statsData?.affiliateName,
                                    payoutEarned: statsData?.payoutEarned || 0,
                                    payoutMade: statsData?.payoutMade || 0,
                                    sales: statsData?.sales || 0,
                                    source: OFFER_SOURCE.TUNE,
                                    status: getStatusForAffiliateLinkMember(statsData || member),
                                    memberStatus: member.affiliate ? getMemberStatus(member.affiliate.status) : MemberStatus.ACTIVE,
                                    linkStatusError: member.updateLog,
                                    additionalDeepLinks: member.affiliateDeeplinks || [],
                                    defaultDeeplink: member.deeplinkUrl,
                                    deletedDate: member.deletedDate,
                                    currencies: linkStats?.baseCurrencies,
                                    avgSaleBase: statsData?.avgSaleBase || 0,
                                    salesBase: statsData?.salesBase || 0,
                                    payoutEarnedBase: statsData?.payoutEarnedBase || 0,
                                    offerAutomationConfigId: member.offerAutomationConfigId,
                                    boostDataForAffiliateOffer: member.boostDataForAffiliateOffer || [],
                                    createdDate: member.createdDate,
                                };
                            }
                        }).filter(Boolean);

                        processedOfferMembers = [...processedOfferMembers, ...processedLinkMembers];
                        processedDeletedMembers = [...processedDeletedMembers, ...deletedLinkMembers];
                    }
                    break;
            }
        }

        return {
            offerMembers: processedOfferMembers,
            offerStats: calculatedOfferStats,
            deletedMembers: processedDeletedMembers,
        };
    }, [basicData, affiliatesData, source]);

    // Create merged offer data for component compatibility
    const mergedOffer = useMemo(() => {
        if (!basicData?.offer?.offer) {
            return null;
        }

        const offer = { ...basicData.offer.offer }; // Create a copy to avoid mutating the original

        // Merge affiliates data into the offer structure for component compatibility
        if (affiliatesData?.affiliates) {
            const { promoAffiliates, linkAffiliatesStats } = affiliatesData.affiliates;

            // Add promos data to offer if it doesn't exist
            if (!offer.promos && promoAffiliates?.length > 0) {
                offer.promos = [
                    {
                        id: offer.id,
                        name: offer.name,
                        defaultPayoutId: null, // Will be set from affiliates data
                        affiliates: promoAffiliates,
                        affiliateStats: promoAffiliates,
                        // Add other required promo fields
                        codePrefix: '',
                        codeSuffix: '',
                        endDate: offer.expirationDate,
                        flatPayout: 0,
                        isOneTime: false,
                        isSubscription: false,
                        isUngrouped: false,
                        multiShopifyEnabled: false,
                        payoutType: 'FLAT',
                        percentPayout: 0,
                        prefixType: 'NONE',
                        priceRuleAmount: 0,
                        priceRuleType: 'NONE',
                        recurringCycleLimit: 0,
                        source: 'SHOPIFY',
                        specialLimitNewCustomersOnly: false,
                        specialLimitOnePerSale: false,
                        specialLimitUsageCapAmount: 0,
                        specialLimitUsageCapEnabled: false,
                        startDate: offer.createdDate,
                        status: 'ACTIVE',
                        usageLimitAmount: 0,
                        usageLimitRule: 'NONE',
                        offerId: offer.id,
                    },
                ];
            }

            // Add links data to offer if it doesn't exist
            if (!offer.links && linkAffiliatesStats?.length > 0) {
                offer.links = [
                    {
                        id: offer.id,
                        affiliateOfferId: offer.id,
                        defaultPayoutId: null, // Will be set from affiliates data
                        affiliateStats: linkAffiliatesStats,
                        affiliates: linkAffiliatesStats.map((stat) => ({
                            id: stat.affiliateOfferId,
                            affiliateId: stat.affiliateId,
                            affiliate: {
                                id: stat.affiliateId,
                                name: stat.affiliateName,
                                email: stat.affiliateEmail,
                                imageUrl: stat.affiliateImageUrl,
                                status: 'ACTIVE',
                                deletedDate: null,
                            },
                            affiliateOfferId: stat.affiliateOfferId,
                            payoutId: stat.payoutId,
                            status: stat.linkStatus,
                            boostDataForAffiliateOffer: stat.boostDataForAffiliateOffer || [],
                        })),
                    },
                ];
            }
        }

        return offer;
    }, [basicData, affiliatesData]);
    const filteredOfferMembers = offerMembers.filter((element) => element !== undefined);
    const filteredDeletedMembers = deletedMembers?.filter((element) => element !== undefined);

    // Set offer members in context when data is ready
    useEffect(() => {
        if (filteredOfferMembers && setOfferMembers) {
            setOfferMembers([...filteredOfferMembers]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredOfferMembers.length, setOfferMembers]);

    // Call onDeletedMembersChange when deleted members change
    const previousDeletedMembersRef = React.useRef<readonly TOfferMember[]>([]);
    useEffect(() => {
        if (filteredDeletedMembers && onDeletedMembersChange) {
            // Only call if the length or content actually changed
            const prevLength = previousDeletedMembersRef.current.length;
            if (filteredDeletedMembers.length !== prevLength ||
                JSON.stringify(filteredDeletedMembers) !== JSON.stringify(previousDeletedMembersRef.current)) {
                previousDeletedMembersRef.current = filteredDeletedMembers;
                onDeletedMembersChange(filteredDeletedMembers);
            }
        }
    }, [filteredDeletedMembers, onDeletedMembersChange]);
    /** FIXME: find pending offer count for both links and promos when offer is isPromoLink */
    const pendingMemberCount = useMemo(
        (): number => filteredOfferMembers.filter(memberIsPending).length,
        [filteredOfferMembers],
    );
    const pendingLinks = useMemo((): boolean => {
        if (!affiliatesData?.affiliates) {
            return false;
        }

        const { promoAffiliates, linkAffiliatesStats } = affiliatesData.affiliates;

        // If we don't have both promo affiliates and link stats, return false
        if (!promoAffiliates?.length || !linkAffiliatesStats?.length) {
            return false;
        }

        // Check if any link affiliate stats have a pending status
        const hasPendingLinks = linkAffiliatesStats.some((linkStat) => {
            const linkStatus = linkStat.linkStatus?.toUpperCase();
            return linkStatus === AFFILIATE_OFFER_LINK_STATUS.PENDING;
        });

        return hasPendingLinks;
    }, [affiliatesData]);
    useEffect(() => {
        if (pendingMemberCount > 0 || pendingLinks) {
            // Start polling by refetching affiliates data
            const interval = setInterval(() => {
                if (offerId && basicData?.offer?.offer) {
                    const offer = basicData.offer.offer;
                    const isLinkOffer = offer.type === OFFER_TYPE.LINK;
                    const isPromoLinkOffer = offer.isPromoLink;

                    client
                        .query({
                            query: GET_OFFER_AFFILIATES_QUERY,
                            variables: {
                                offerId,
                                startDate: dateRangeVariables.startDate,
                                endDate: dateRangeVariables.endDate,
                                isLinkOffer,
                                isPromoLinkOffer,
                            },
                            fetchPolicy: 'network-only',
                        })
                        .then((result) => {
                            setAffiliatesData(result.data);
                        })
                        .catch((err) => {
                            logger.error(err);
                        });
                }
            }, 2000);

            return () => clearInterval(interval);
        } else {
            setDisableOfferEdit(false);
        }
    }, [pendingMemberCount, setDisableOfferEdit, pendingLinks, client, offerId, basicData, dateRangeVariables]);

    // get the list of selected members for the wizard
    const selectedMembers = useMemo(() => {
        if (isEmpty(selectedMemberIds)) {
            return map(
                filteredOfferMembers,
                (m): TMember => ({
                    affiliateOfferId: m.affiliateOfferId,
                    affiliateId: m.affiliateId,
                    firstName: '',
                    id: m.memberId,
                    inOffer: true,
                    payoutId: m.payoutId,
                    instagramUsername: '',
                    lastName: '',
                    name: m.name,
                    previousCode: {
                        code: m.source === OFFER_SOURCE.SHOPIFY ? m.code : '',
                        end: m.source === OFFER_SOURCE.SHOPIFY ? m.codeEndDate : null,
                        start: m.source === OFFER_SOURCE.SHOPIFY ? m.codeStartDate : null,
                    },
                    forceCheckIn: false,
                    programIds: [],
                    status: m.status,
                    additionalDeeplinks: map((m as IAffiliateLinkMember).additionalDeepLinks, (deeplink) => ({
                        id: deeplink.id,
                        label: deeplink.label,
                        url: deeplink.url,
                    })),
                    boostDataForAffiliateOffer: m.boostDataForAffiliateOffer,
                }),
            );
        }
        return reduce(
            filteredOfferMembers,
            (prev, next) => {
                if (includes(selectedMemberIds, next.memberId)) {
                    prev.push({
                        affiliateOfferId: next.affiliateOfferId,
                        affiliateId: next.affiliateId,
                        firstName: '',
                        id: next.memberId,
                        inOffer: true,
                        payoutId: next.payoutId,
                        instagramUsername: '',
                        lastName: '',
                        name: next.name,
                        programIds: [],
                        previousCode: {
                            end: next.source === OFFER_SOURCE.SHOPIFY ? next.codeEndDate : null,
                            code: next.source === OFFER_SOURCE.SHOPIFY ? next.code : '',
                            start: next.source === OFFER_SOURCE.SHOPIFY ? next.codeStartDate : null,
                        },
                        forceCheckIn: false,
                        status: next.status,
                        additionalDeeplinks: map((next as IAffiliateLinkMember).additionalDeepLinks, (deeplink) => ({
                            id: deeplink.id,
                            label: deeplink.label,
                            url: deeplink.url,
                        })),
                        boostDataForAffiliateOffer: next.boostDataForAffiliateOffer,
                    });
                }
                return prev;
            },
            [] as TMember[],
        );
    }, [filteredOfferMembers, selectedMemberIds]);

    const failedMembers = useMemo(
        (): readonly TMember[] =>
            map(
                filter(filteredOfferMembers, (member: TOfferMember) => member.status === OfferMemberStatus.FAILED),
                (member): TMember => ({
                    affiliateOfferId: member.affiliateOfferId,
                    firstName: '',
                    affiliateId: member.affiliateId,
                    id: member.memberId,
                    payoutId: member.payoutId,
                    inOffer: true,
                    instagramUsername: '',
                    lastName: '',
                    name: member.name,
                    programIds: [],
                    previousCode: {
                        end: member.source === OFFER_SOURCE.SHOPIFY ? member.codeEndDate : null,
                        code: member.source === OFFER_SOURCE.SHOPIFY ? member.code : '',
                        start: member.source === OFFER_SOURCE.SHOPIFY ? member.codeStartDate : null,
                    },
                    forceCheckIn: false,
                    promoCodeStatus: member.promoCodeStatus,
                }),
            ),
        [filteredOfferMembers],
    );

    const failedPromoCodeErrors = useMemo(() => {
        if (!affiliatesData?.affiliates) {
            return [];
        }
        switch (source) {
            case OFFER_SOURCE.SHOPIFY:
                const { promoAffiliates } = affiliatesData.affiliates;
                if (!promoAffiliates) {
                    logger.warn('Offer source is Shopify but missing promo affiliates.');
                    return [];
                }

                const failedAffiliates = promoAffiliates.filter((affiliate) => {
                    const status = getStatusForPromoCodeMember(affiliate);
                    return status === OfferMemberStatus.FAILED;
                });

                const errors = failedAffiliates
                    .map(({ affiliateOfferPromoStatus }) => affiliateOfferPromoStatus)
                    .filter((status) => status != null && status !== undefined);

                return errors;
            default:
                return [];
        }
    }, [affiliatesData, source]);
    const [fixingFailedMembers, setFixingFailedMembers] = useState(false);

    if (error) {
        return {
            error: new Error('Oops! Something went wrong. Please refresh or try again later.'),
            status: OfferDetailsQueriesStatus.Failed,
        };
    }

    if (!basicData) {
        return {
            status: OfferDetailsQueriesStatus.Loading,
        };
    }

    return {
        failedPromoCodeErrors,
        failedMembers: {
            members: failedMembers,
            dismissFailedMembers: () => {
                setFixingFailedMembers(false);
            },
            showFailedMembers: () => {
                if (isEmpty(failedMembers)) {
                    return;
                }
                setFixingFailedMembers(true);
            },
            visible: fixingFailedMembers,
        },
        missingShopifyCredentials,
        memberTable: {
            buttonActions,
            disableCompose: isEmpty(filteredOfferMembers) || pendingMemberCount > 0,
            onSelectMembers: updatedSelectedMemberIds,
            selectedAffiliateIds: selectedMemberIds,
        },
        offerDetails: mergedOffer || basicData.offer.offer,
        offerMembers: filteredOfferMembers,
        deletedMembers: filteredDeletedMembers,
        offerStats,
        multipleShopifyOfferStats: isEnabledMultipleShopify && multipleOfferStats ? multipleOfferStats : [],
        refresh: () => {
            // Refetch basic data (includes offer stats) using refetch
            refetchBasicData();

            // Refetch affiliates data using refetch
            refetchAffiliatesData();

            // Refetch boost data using refetch
            refetchBoostData();
        },
        status: OfferDetailsQueriesStatus.Ready,
        updateMembers: {
            mode: updateWizardMode,
            onClose: () => {
                setUpdateWizardVisible(false);
            },
            refreshDatesOnly,
            selectedMembers,
            visible: updateWizardVisible,
        },
        updateManageOffer: {
            mode: updateWizardMode,
            onClose: () => {
                setIsVisibleSelectOffer(false);
            },
            selectedMembers,
            visible: isVisibleManageOffer,
            setIsVisibleSelectOffer,
        },
        memberConversion: {
            visible: isVisibleMemberConversion,
            selectedMemberConversion,
            onClose: () => {
                setIsVisibleMemberConversion(false);
            },
        },
        multipleShopifyAffiliateStats: {
            visible: showStoreLevelAffiliateStats,
            affiliatesStats,
            toggle: () => {
                setShowStoreLevelAffiliateStats(!showStoreLevelAffiliateStats);
            },
        },
        shopifyStoreName: advertiserShopifyScopeCheck.data?.advertiser.shopifyResources[0]?.identifier ?? null,
        affiliatesData: affiliatesData?.affiliates, // Add affiliatesData to the return object
        boostData: localBoostData?.boostDataForAllAffiliatesInOffer || [], // Add boost data to the return object
        isLoadingMembers,
    };
};
